import React, { useEffect, useMemo, useState } from 'react';
import { updateUsersFilter } from '../../data/stores/companyStore';
import usePagination from '../../hooks/usePaginationStore';
import { fetchUsers, sendInvite } from '../../data/thunks/userThunk';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../data/store';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import {
	CrewRoles,
	User,
	UserPermissions,
	UserStatus,
} from '../../data/models/user';
import EditInternalCrewModal from './EditInternalCrewModal';
import Table from '../common/Table';
import { GridColDef } from '@mui/x-data-grid';
import { Box, IconButton, Typography, Button } from '@mui/material';
import { styled } from 'styled-components';
import ScreenBusySpinner from '../common/ScreenBusySpinner';
import GroupsCell from '../common/GroupsCell';
import { isAdmin } from '../../utils/auth-utils';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { getUser } from '../../services/user-service';

export default function InternalCrew() {
	const dispatch: AppDispatch = useDispatch();

	const { users } = useSelector((state: RootState) => state.companyState);

	const [editModal, setEditModal] = useState<false | User>(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSearchLoading, setIsSearchLoading] = useState(false);
	const [selectedRows, setSelectedRows] = useState<string[]>([]);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState<
		Record<string, boolean>
	>({});

	const {
		searchText,
		setSearchText,
		page,
		setPage,
		pageSize,
		sortModel,
		setSortModel,
		where,
		setWhere,
	} = usePagination('companyState', 'users', updateUsersFilter);

	useEffect(() => {
		const filter = {
			searchText,
			order: sortModel[0]?.field,
			direction: sortModel[0]?.sort,
			page: page,
			take: pageSize,
			where,
			include: {
				groups: true,
			},
		};
		dispatch(fetchUsers(filter));
	}, []);

	const handleTextSearch = async (newSearchText: string) => {
		setSearchText(newSearchText);
		setPage(0);
		const filter = {
			searchText: newSearchText,
			order: 'created',
			direction: 'DESC',
			page: 0,
			take: pageSize,
			include: {
				groups: true,
			},
		};
		setIsSearchLoading(true);
		try {
			await dispatch(fetchUsers(filter));
		} finally {
			setIsSearchLoading(false);
		}
	};

	const handleSortOnChange = (order) => {
		setSortModel(order);
		const filter = {
			searchText,
			order: order[0]?.field,
			direction: order[0]?.sort,
			page: page,
			take: pageSize,
			where,
			include: {
				groups: true,
			},
		};
		dispatch(fetchUsers(filter));
	};

	const handleOnPageChange = (page, scrollToTop = false) => {
		const filter = {
			searchText,
			order: sortModel[0]?.field,
			direction: sortModel[0]?.sort,
			page: page,
			take: pageSize,
			where,
			include: {
				groups: true,
			},
		};
		setPage(page);
		dispatch(fetchUsers(filter));
		if (scrollToTop) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	const handleUserOnEdit = async (id: string) => {
		setIsLoading(true);
		try {
			const user = await getUser(id);
			if (user) {
				setEditModal(user);
			}
		} catch (error) {
			console.error('Error fetching user:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleColumnVisibilityModelChange = (
		newModel: Record<string, boolean>
	) => {
		setColumnVisibilityModel(newModel);
	};

	const handleBulkRequestChanges = async () => {
		if (selectedRows.length === 0) return;

		try {
			const promises = selectedRows.map((userId) =>
				dispatch(sendInvite(userId))
			);
			await Promise.all(promises);
			setSelectedRows([]);
		} catch (error) {
			console.error('Error sending invites:', error);
		}
	};

	const renderBulkActions = () => {
		const hasSelection = selectedRows.length > 0;

		if (!hasSelection) return <></>;

		return (
			<BulkActionsButtonContainer>
				<Button
					variant="outlined"
					size="small"
					startIcon={<SendIcon />}
					onClick={handleBulkRequestChanges}
					disabled={!hasSelection}
					sx={{ marginRight: 1 }}
				>
					Request Information ({selectedRows.length})
				</Button>
			</BulkActionsButtonContainer>
		);
	};

	const columns: GridColDef[] = useMemo(() => {
		return [
			{
				field: 'name',
				headerName: 'Name',
				minWidth: 200,
				disableColumnMenu: true,
			},
			{
				field: 'email',
				headerName: 'Email',
				minWidth: 250,
				disableColumnMenu: true,
			},
			{
				field: 'permissions',
				headerName: 'Permission',
				disableColumnMenu: true,
				renderCell: (params) =>
					UserPermissions[params.value]?.display || params.value,
			},
			{
				field: 'role',
				headerName: 'Role',
				minWidth: 175,
				disableColumnMenu: true,
				renderCell: (params) =>
					CrewRoles[params.value]?.display || params.value,
			},
			{
				field: 'alwaysAddToProjects',
				headerName: 'Always Add to Projects',
				minWidth: 200,
				disableColumnMenu: true,
				align: 'center',
				headerAlign: 'left',
				renderCell: (params) => (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-start',
							alignItems: 'center',
							height: '100%',
						}}
					>
						{params.value ? <CheckIcon /> : <CloseIcon />}
					</Box>
				),
			},
			{
				field: 'status',
				headerName: 'Status',
				disableColumnMenu: true,
				renderCell: (params) => {
					return <UserStatusCell value={params.value} />;
				},
			},
			{
				field: 'groups',
				headerName: 'Groups',
				minWidth: 270,
				disableColumnMenu: true,
				renderCell: (params) => <GroupsCell groups={params.value} />,
			},
			{
				field: 'Actions',
				headerName: '',
				disableColumnMenu: true,
				hideable: false,
				align: 'center',
				headerAlign: 'center',
				renderCell: (params: any) =>
					isAdmin() ? (
						<IconButton
							onClick={() => handleUserOnEdit(params.id)}
							color={'primary'}
							aria-label="Edit User"
						>
							<EditIcon />
						</IconButton>
					) : (
						<></>
					),
			},
		];
	}, [handleUserOnEdit]);

	const rows = useMemo(() => {
		return users.list.map((user) => ({
			id: user.id,
			name: `${user.firstName} ${user.lastName}`,
			email: user.email,
			role: user.role,
			permissions: user.permissions,
			status: user.status,
			alwaysAddToProjects: user.alwaysAddToProjects,
			groups: user.groups || [],
		}));
	}, [users.list]);

	return (
		<div>
			<EditInternalCrewModal
				open={!!editModal}
				user={editModal || undefined}
				handleClose={() => setEditModal(false)}
			/>
			<Table
				columns={columns}
				rows={rows}
				page={page}
				pageSize={pageSize}
				rowCount={users.count}
				sortModel={sortModel}
				onSortModelChange={handleSortOnChange}
				searchText={searchText}
				handleTextSearch={handleTextSearch}
				onPaginationModelChange={handleOnPageChange}
				loading={!users.initialized || isLoading || isSearchLoading}
				checkboxSelection={true}
				selectionModel={selectedRows}
				onSelectionModelChange={setSelectedRows}
				bulkActions={renderBulkActions()}
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={
					handleColumnVisibilityModelChange
				}
				enableColumnVisibilityToggle={true}
				disableRowSelectionOnClick={true}
			/>
		</div>
	);
}

const UserStatusCell = ({ value }: { value: string }) => {
	const status = UserStatus[value];
	return (
		<StatusDisplay $isUnsubscribed={value === 'UNSUBSCRIBED'}>
			{status ? status.display : value}
		</StatusDisplay>
	);
};

const StatusDisplay = styled.div<{ $isUnsubscribed: boolean }>`
	color: ${({ theme, $isUnsubscribed }) =>
		$isUnsubscribed ? theme.palette.error.main : 'inherit'};
`;

const BulkActionsButtonContainer = styled.div`
	display: flex;
	gap: 8px;
	align-items: center;
`;
